/*
 * Shrinks a product-card button label until it fits, so a long label (Finnish, Latvian, "Greitas
 * pridėjimas") never wraps, clips or touches the button's edge. Only a label that does not fit
 * shrinks; the rest keep the button's font size. CSS cannot know how wide a translated string is,
 * so the label is measured and the result is set as --fit-size on the label.
 *
 * Covers cards that arrive later (load more, filtering, AJAX collections) and label swaps
 * (add to cart -> sold out) through a MutationObserver, and width changes through a ResizeObserver.
 */
(() => {
  const BUTTON = '.product-column .button--product-action';
  const LABEL = '.button--text:not(.visually-hidden), .product-column_quick-btn-text:not(.visually-hidden)';
  const FLOOR = 11;     // px, the smallest label size
  const SLACK = 1;      // px kept free beside the label

  const pending = new Set();
  let frame = 0;

  const resizeObserver = new ResizeObserver((entries) => entries.forEach((e) => queue(e.target)));

  function queue(button) {
    pending.add(button);
    if (!frame) frame = requestAnimationFrame(flush);
  }

  function watch(root) {
    if (root.nodeType !== 1) return;
    const buttons = root.matches(BUTTON) ? [root] : [];
    root.querySelectorAll(BUTTON).forEach((b) => buttons.push(b));
    buttons.forEach((b) => {
      resizeObserver.observe(b);
      queue(b);
    });
  }

  // Buttons removed by filtering or load more stop being observed. A node that was only moved is
  // watched again by the addedNodes pass; a plain text or detached node costs one matches() call.
  function unwatch(root) {
    if (root.nodeType !== 1 || !root.querySelector) return;
    if (root.matches(BUTTON)) resizeObserver.unobserve(root);
    root.querySelectorAll(BUTTON).forEach((b) => resizeObserver.unobserve(b));
  }

  // Read every size first, then write, so the batch costs one layout.
  function flush() {
    frame = 0;
    const buttons = [...pending];
    pending.clear();
    const writes = [];
    buttons.forEach((button) => {
      if (!button.isConnected || !button.clientWidth) return;
      const base = parseFloat(getComputedStyle(button).fontSize);
      const content = room(button) - paddingX(button);
      button.querySelectorAll(LABEL).forEach((label) => {
        const wrap = label.parentElement;
        const taken = [...wrap.children].reduce((sum, el) => {
          if (el === label || getComputedStyle(el).display === 'none') return sum;
          return sum + el.getBoundingClientRect().width + gap(wrap);
        }, 0);
        const current = parseFloat(getComputedStyle(label).fontSize);
        const natural = (label.getBoundingClientRect().width * base) / current; // width at the base size
        const avail = content - taken - SLACK;
        if (!natural || avail <= 0) return;
        const size = natural <= avail ? base : Math.max(FLOOR, Math.floor((base * avail / natural) * 10) / 10);
        writes.push([label, size === base ? '' : size + 'px']);
      });
    });
    writes.forEach(([label, size]) => {
      if (size) label.style.setProperty('--fit-size', size);
      else label.style.removeProperty('--fit-size');
      label.setAttribute('data-fit', '');
    });
  }

  // The width the card gives the button. A button that sizes to its label (the card_view layout) would
  // shrink with every shrunk label, so it is measured against its .product-column_button-wrap. A wrap that
  // sizes to the button too (no grow, no min-width) is measured against its whole row; siblings that flex
  // would otherwise give the same circle.
  function room(button) {
    const wrap = button.closest('.product-column_button-wrap');
    if (!wrap) return button.clientWidth;
    const ws = getComputedStyle(wrap);
    let width = wrap.clientWidth - parseFloat(ws.paddingLeft) - parseFloat(ws.paddingRight);
    const row = wrap.parentElement;
    if (row && ws.flexGrow === '0' && (ws.minWidth === 'auto' || ws.minWidth === '0px')) {
      const rs = getComputedStyle(row);
      width = row.clientWidth - parseFloat(rs.paddingLeft) - parseFloat(rs.paddingRight);
    }
    return Math.max(button.clientWidth, width);
  }

  function paddingX(el) {
    const cs = getComputedStyle(el);
    return parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
  }

  function gap(el) {
    return parseFloat(getComputedStyle(el).columnGap) || 0;
  }

  new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.removedNodes.forEach(unwatch);
      m.addedNodes.forEach(watch);
      const host = (m.target.nodeType === 1 ? m.target : m.target.parentElement);
      const button = host && host.closest(BUTTON);
      if (button) queue(button);
    });
  }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });

  const start = () => watch(document.documentElement);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Widths change once the web font arrives.
  if (document.fonts) {
    document.fonts.ready.then(() => document.querySelectorAll(BUTTON).forEach(queue));
    document.fonts.addEventListener('loadingdone', () => document.querySelectorAll(BUTTON).forEach(queue));
  }
})();
